import { hasDisplayIdPrefix } from '@pkgs/api/utils/strings';
import { getConfigWithMetadata } from '@pkgs/api/utils/headers';
import { type ApiConfig, type ApiResult } from '@pkgs/api/types';
import { type AutoSuggestionListingResponse, type AutoSuggestionPlaceResponse, type Language, type PlaceType } from '@generated/search-bff';

import { getSuggestionsByDisplayId } from '../getSuggestionsByDisplayId';
import { getPlaceSuggestions } from '../getPlaceSuggestions';

export type GetSuggestionsOptions = {
  limit: number;
  language: Language;
  sessionToken?: string;
  types?: PlaceType[];
  /** Filter results to only include results in supported countries */
  supportedCountryFiltering?: boolean;
  /** Also apply the types filter to the custom places */
  filterCustomPlaceTypes?: boolean;
  /** Restrict results to propertyFinder places, which were created for Dubai */
  onlyPropertyFinderPlaces?: boolean;
};

export type ListingSuggestions = {
  type: 'properties';
  data: AutoSuggestionListingResponse[];
};

export type PlaceSuggestions = {
  type: 'locations';
  data: AutoSuggestionPlaceResponse[];
};

export type SuggestionResult = ListingSuggestions | PlaceSuggestions;

/**
 * Fetch either property listing suggestions or place suggestions based on the provided query using either the `/api/v2/autosuggestion/listing/suggest` or `/api/v2/autosuggestion/suggest` endpoint.
 *
 * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/autosuggestion/getSuggestions Search API Documentation}
 */
export async function getSuggestions(
  query: string,
  options: GetSuggestionsOptions,
  apiConfig: ApiConfig,
): Promise<ApiResult<SuggestionResult>> {
  const _apiConfig = getConfigWithMetadata(apiConfig, 'getSuggestions');

  if (hasDisplayIdPrefix(query)) {
    const response = await getSuggestionsByDisplayId(query, options, _apiConfig);
    return response.status === 'success' ? { status: 'success', data: { type: 'properties', data: response.data } } : response;
  } else {
    const response = await getPlaceSuggestions(query, options, _apiConfig);
    return response.status === 'success' ? { status: 'success', data: { type: 'locations', data: response.data } } : response;
  }
}
